import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { get, set, over, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { financialProductResidualGridValidator } from 'features/financialProducts/validators';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { FINANCIAL_PRODUCT_RESIDUAL_GRIDS_QUERY } from '../../../queries';
import {
  REMOVE_FINANCIAL_PRODUCT_RESIDUAL_GRID,
  ADD_UPDATE_FINANCIAL_PRODUCT_RESIDUAL_GRID
} from 'features/financialProducts/edit/mutations';
import { FINANCIAL_PRODUCT_RESIDUAL_GRID_DELETED, FINANCIAL_PRODUCT_RESIDUAL_GRID_UPDATED } from '../../../subscriptions';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { remove } from 'ramda';
import FinancialProductResidualGridsTable from './FinancialProductResidualGridsTable';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const FinancialProductResidualGridsContainer = ({ productId, residualGrids, isNewFinancialProduct, canWrite }) => {
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);
  const [t] = useTranslation();
  const addToast = useToast();

  const [residualGridsValidation, validateResidualGrids, resetValidation] = useDirtyFieldValidation(
    financialProductResidualGridValidator,
    dirtyInfo
  );

  useEffect(() => {
    resetLens(
      residualGrids?.map(element => {
        const elementLocal = { ...element };
        elementLocal.isInEditing = false;
        elementLocal.isSaving = false;
        return elementLocal;
      }) || []
    );
  }, [resetLens, residualGrids]);

  useEffect(() => {
    validateResidualGrids(lens |> get, dirtyInfo);
  }, [lens, validateResidualGrids, dirtyInfo]);

  const { data: residualGridsData, refetch: refetchResidualGrids } = useQuery(FINANCIAL_PRODUCT_RESIDUAL_GRIDS_QUERY);

  const onLanguageChange = useCallback(() => {
    refetchResidualGrids();
  }, [refetchResidualGrids]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  const [saveResidualGrid] = useMutation(ADD_UPDATE_FINANCIAL_PRODUCT_RESIDUAL_GRID);

  const [deleteResidualGrid] = useMutation(REMOVE_FINANCIAL_PRODUCT_RESIDUAL_GRID);

  useSubscription(FINANCIAL_PRODUCT_RESIDUAL_GRID_DELETED, {
    onData: ({ client }) => {
      addToast(t('FinancialProduct.Details.ResidualValueGrids.ResidualGridDeleted'), 'success');
      client.cache.evict({
        id: `FinancialProductItem:{"productId":${productId}}`,
        fieldName: 'financialProductResidualValueGrids'
      });
      client.cache.gc();
    }
  });

  useSubscription(FINANCIAL_PRODUCT_RESIDUAL_GRID_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.ResidualValueGrids.ResidualGridUpdated'), 'success');
      client.cache.modify({
        id: `FinancialProductItem:{"productId":${data.financialProductResidualGridUpdated.productId}}`,
        fields: {
          financialProductResidualValueGrids: (previous, { toReference }) => {
            if (previous.some(x => x.__ref === toReference(data.financialProductResidualGridUpdated)?.__ref)) return [...previous];
            return [...previous, toReference(data.financialProductResidualGridUpdated)];
          }
        }
      });
      set(lens)(
        (lens |> get).map(item =>
          item.financialProductXResidualValueGridId === data?.financialProductResidualGridUpdated?.financialProductXResidualValueGridId
            ? { ...item, isSaving: false, isInEditing: false }
            : item
        )
      );
    }
  });

  const handleCancelEdit = useCallback(
    (financialProductXResidualValueGridId, orderNo) => {
      if (!financialProductXResidualValueGridId) resetLens(remove(orderNo, 1, lens |> get));
      else {
        const original = residualGrids.find(x => x.financialProductXResidualValueGridId === financialProductXResidualValueGridId);
        over(lens[orderNo], prevValue => ({ ...prevValue, ...original, isInEditing: false }));
      }
    },
    [lens, residualGrids, resetLens]
  );

  const handleAdd = useCallback(() => {
    resetLens([
      ...(lens |> get),
      {
        productId: productId,
        gridId: null,
        maximumDecrease: 0,
        maximumIncrease: 0,
        isSaving: false,
        isInEditing: true
      }
    ]);
    resetValidation();
  }, [lens, productId, resetLens, resetValidation]);

  const handleDelete = useCallback(
    (financialProductXResidualValueGridId, orderNo) => {
      if (!financialProductXResidualValueGridId) resetLens(remove(orderNo, 1, lens |> get));
      else if (window.confirm(t('FinancialProduct.Details.ResidualValueGrids.DeleteResidualGridConfirmation'))) {
        deleteResidualGrid({
          variables: {
            input: { financialProductXResidualValueGridId }
          }
        });
      }
    },
    [deleteResidualGrid, lens, resetLens, t]
  );

  const handleSave = useCallback(
    orderNo => {
      if ((lens |> get)[orderNo].gridId === null) {
        validateResidualGrids(lens |> get);
        return;
      }
      set(lens[orderNo].isSaving)(true);
      const elementTL = [...(lens |> get)][orderNo];
      saveResidualGrid({
        variables: {
          input: {
            financialProductXResidualValueGridId: elementTL.financialProductXResidualValueGridId,
            productId: elementTL.productId,
            gridId: elementTL.gridId,
            gridName: elementTL.gridName,
            maximumDecrease: elementTL.maximumDecrease,
            maximumIncrease: elementTL.maximumIncrease
          }
        }
      });
    },
    [lens, validateResidualGrids, saveResidualGrid]
  );

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return (
    <FinancialProductResidualGridsTable
      canWrite={canWrite}
      isNewFinancialProduct={isNewFinancialProduct}
      onAdd={handleAdd}
      onCancelEdit={handleCancelEdit}
      onDelete={handleDelete}
      onSave={handleSave}
      residualGrids={lens}
      residualValueGridList={residualGridsData ? residualGridsData.residualValueGridDictionary : []}
      validation={residualGridsValidation}
      isDirty={isDirty}
    />
  );
};

FinancialProductResidualGridsContainer.propTypes = {
  productId: PropTypes.number.isRequired,
  residualGrids: PropTypes.array.isRequired,
  isNewFinancialProduct: PropTypes.bool.isRequired,
  canWrite: PropTypes.bool.isRequired
};

export default FinancialProductResidualGridsContainer;
